import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueries } from 'react-query';
import {
  Card,
  CardContent,
  Chip,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Container } from 'Components/Container';
import { PageLoaderList } from 'Components/PageLoader/List';
import { NoData } from 'Components/NoData';
import { UsageBar } from 'Components/UsageBar';
import { getOpsDashboard } from 'services/company-service';
import { getPolicyMetrics } from 'services/policies-service';
import { getGaMetrics } from 'services/gap-analysis';
import { getUserMetrics } from 'services/users-service';
import { getCookieMetrics } from 'services/cookies-service';

const SummaryCard = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ flex: 1 }}>
    <CardContent sx={{ textAlign: 'center' }}>
      <Typography variant="h4" sx={{ color: color ?? 'text.primary', fontWeight: 700 }}>
        {value ?? '—'}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </CardContent>
  </Card>
);

export const OpsDashboard = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);

  const { isLoading, error, data: dashboard } = useQuery(
    ['ops-dashboard', page],
    () => getOpsDashboard(page, 20).then(r => r.data)
  );

  useEffect(() => {
    if (error && error.response?.status === 403) {
      navigate('/no-permission');
    }
  }, [error, navigate]);

  const tenants = dashboard?.data ?? [];

  const metricsQueries = useQueries(
    tenants.map(tenant => ({
      queryKey: ['ops-metrics', tenant.slug],
      queryFn: () =>
        Promise.all([
          getPolicyMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalPolicies: null })),
          getGaMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalFrameworks: null })),
          getUserMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalUsers: null })),
          getCookieMetrics(tenant.slug).then(r => r.data).catch(() => ({ totalDisclaimers: null })),
        ]),
      staleTime: 5 * 60 * 1000,
    }))
  );

  const getMetricsForTenant = (idx) => {
    const q = metricsQueries[idx];
    if (!q?.data) return { policy: null, ga: null, users: null, cookies: null };
    const [policy, ga, users, cookies] = q.data;
    return { policy, ga, users, cookies };
  };

  const summary = dashboard?.summary;

  return (
    <Container
      title="Operations Dashboard"
      breadcrumb="Configurações Gerais / Operations Dashboard"
    >
      {isLoading && <PageLoaderList />}
      {error && <Typography color="error">Erro ao carregar dados.</Typography>}

      {!isLoading && !error && summary && (
        <>
          <Stack direction="row" spacing={2} sx={{ mt: 3, mb: 3 }}>
            <SummaryCard label="Total Ativos" value={summary.totalActive} color="#4caf50" />
            <SummaryCard label="Total Inativos" value={summary.totalInactive} color="#9e9e9e" />
            <SummaryCard label="Free" value={summary.planDistribution?.free ?? 0} />
            <SummaryCard label="Starter" value={summary.planDistribution?.starter ?? 0} />
            <SummaryCard label="Premium" value={summary.planDistribution?.premium ?? 0} />
          </Stack>

          {tenants.length === 0 ? (
            <NoData description="Nenhum tenant encontrado" />
          ) : (
            <>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Plano</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Políticas</TableCell>
                    <TableCell>Gap Analysis</TableCell>
                    <TableCell>Usuários</TableCell>
                    <TableCell>Cookies</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant, idx) => {
                    const { policy, ga, users, cookies } = getMetricsForTenant(idx);
                    const limits = tenant.planLimits;
                    return (
                      <TableRow key={tenant._id} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{tenant.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{tenant.slug}</Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={tenant.signedPlan ?? '—'} size="small" variant="outlined" />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={tenant.active ? 'Ativo' : 'Inativo'}
                            size="small"
                            color={tenant.active ? 'success' : 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <UsageBar
                            current={policy?.totalPolicies ?? 0}
                            limit={limits?.maxPoliciesLimit}
                          />
                        </TableCell>
                        <TableCell>
                          <UsageBar
                            current={ga?.totalFrameworks ?? 0}
                            limit={limits?.maxGapAnalysisLimit}
                          />
                        </TableCell>
                        <TableCell>
                          <UsageBar
                            current={users?.totalUsers ?? 0}
                            limit={limits?.maxUsersLimit}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {cookies?.totalDisclaimers ?? '—'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              <Stack sx={{ justifyContent: 'center', flexDirection: 'row', mt: 2 }}>
                <Pagination
                  count={dashboard.totalPages}
                  page={page}
                  onChange={(_, p) => setPage(p)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Stack>
            </>
          )}
        </>
      )}
    </Container>
  );
};
